import { apiClient, type PaginatedResponse } from '../../../api/client'
import { type PaginationOptions } from '../../../api/pagination'
import { OrganizationsorganizationsApi } from '../../../generated-types/apis/OrganizationsorganizationsApi'
import { Configuration } from '../../../generated-types/runtime'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import { createAddress, getPKfromSelf, parseNestedObject } from '../../../utils/functions'
import { type Organization } from '../utils/types'
import { APIOrganizationFromJSON } from '../../../generated-types/models/APIOrganization'
import {
  type APIOrganizationRequest,
  type APIPatchedOrganizationRequest,
} from '../../../generated-types/models'

const log = new LogTool({ context: 'OrganizationAPI', enable: true, logLevel: 'warn' })

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: () => {
      const token = Cookies.get('access')
      return token || ''
    }
  })
}

/**
 * Create API instance
 */
function getOrganizationsApi(): OrganizationsorganizationsApi {
  return new OrganizationsorganizationsApi(getApiConfiguration())
}

function extractAddressUrl(address: unknown): string | undefined {
  if (!address) return undefined

  if (typeof address === 'string') {
    return address.trim() || undefined
  }

  if (typeof address === 'object' && address !== null) {
    const maybeValue = (address as Record<string, unknown>)
    const urlCandidate = maybeValue.value ?? maybeValue.self ?? maybeValue.url

    if (typeof urlCandidate === 'string') {
      return urlCandidate.trim() || undefined
    }
  }

  return undefined
}

function sanitizeWebsite(website: unknown): string | undefined {
  if (typeof website !== 'string') return undefined
  const trimmed = website.trim()
  if (!trimmed) return undefined

  // Ensure protocol is present; keep original if already valid
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    const normalized = new URL(candidate)
    return normalized.toString()
  } catch {
    // Fall back to original input; backend will handle validation
    return trimmed
  }
}

function resolveOrganizationId(idOrSelf: string | number): number {
  if (typeof idOrSelf === 'number') {
    return idOrSelf
  }

  const pk = getPKfromSelf(idOrSelf)
  const id = Number(pk)

  if (Number.isNaN(id)) {
    throw new Error(`Unable to resolve organization id from "${idOrSelf}"`)
  }

  return id
}

/**
 * Create Organization from API response
 */
export function createOrganization(organizationResponse: any): Organization {
  const apiOrganization = APIOrganizationFromJSON(organizationResponse)

  return {
    ...apiOrganization,
    key: getPKfromSelf(apiOrganization.self),
    shippingAddress: parseNestedObject(apiOrganization.shippingAddress, createAddress),
    billingAddress: parseNestedObject(apiOrganization.billingAddress, createAddress),
  }
}

/**
 * Convert form input to API JSON
 */
export function formInputToAPIOrganizationJSON(
  input: any,
  options: { partial?: boolean } = {}
): APIOrganizationRequest | APIPatchedOrganizationRequest {
  const { partial = false } = options

  const name = typeof input?.name === 'string' ? input.name.trim() : undefined
  const vatNumber = typeof input?.vatNumber === 'string' ? input.vatNumber.trim() : undefined
  const website = sanitizeWebsite(input?.website)
  const billingAddress = extractAddressUrl(input?.billingAddress)
  const shippingAddress = extractAddressUrl(input?.shippingAddress)

  const basePayload: APIPatchedOrganizationRequest = {
    ...(billingAddress ? { billingAddress } : {}),
    ...(shippingAddress ? { shippingAddress } : {}),
    ...(vatNumber ? { vatNumber } : {}),
    ...(website ? { website } : {}),
    ...(name ? { name } : {}),
  }

  if (partial) {
    return basePayload
  }

  if (!name) {
    throw new Error('Organization name is required')
  }

  return {
    name,
    ...(billingAddress ? { billingAddress } : {}),
    ...(shippingAddress ? { shippingAddress } : {}),
    ...(vatNumber ? { vatNumber } : {}),
    ...(website ? { website } : {}),
  }
}

/**
 * Fetch paginated organizations from API
 */
export async function fetchOrganizations(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Organization>> {
  log.info('Fetching organizations with options:', options)

  const response = await getOrganizationsApi().v1OrganizationsOrganizationList({
    page: options.page,
    pageSize: options.pageSize,
  })

  const organizations = response.results ? response.results.map(createOrganization) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: organizations,
  }
}

/**
 * Fetch a single organization by ID or URL
 */
export async function fetchOrganization(
  idOrUrl: string | number,
  expand?: string[]
): Promise<Organization> {
  log.info('Fetching organization:', idOrUrl)

  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `organizations/organizations/${idOrUrl}/`
    : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<any>(fetchUrl)
  return createOrganization(response)
}

/**
 * Fetch current user's organization
 */
export async function fetchCurrentUsersOrganization(): Promise<string> {
  log.info('Fetching current users organization')

  const response = await apiClient.get<any>('v1/accounts/users/self/')
  return response.organization || ''
}

/**
 * Create a new organization
 */
export async function createOrganizationApi(organizationData: any): Promise<Organization> {
  log.info('Creating organization:', organizationData)

  const request = formInputToAPIOrganizationJSON(organizationData) as APIOrganizationRequest
  const response = await getOrganizationsApi().v1OrganizationsOrganizationCreate({
    aPIOrganizationRequest: request,
  })

  return createOrganization(response)
}

/**
 * Update an existing organization
 */
export async function updateOrganizationApi(
  idOrSelf: string | number,
  organizationData: any
): Promise<Organization> {
  log.info('Updating organization:', idOrSelf, organizationData)

  const id = resolveOrganizationId(idOrSelf)
  const request = formInputToAPIOrganizationJSON(organizationData) as APIOrganizationRequest
  const response = await getOrganizationsApi().v1OrganizationsOrganizationUpdate({
    id,
    aPIOrganizationRequest: request,
  })
  return createOrganization(response)
}

/**
 * Patch an existing organization (partial update)
 */
export async function patchOrganizationApi(
  idOrSelf: string | number,
  organizationData: Partial<any>
): Promise<Organization> {
  log.info('Patching organization:', idOrSelf, organizationData)

  const id = resolveOrganizationId(idOrSelf)
  const request = formInputToAPIOrganizationJSON(organizationData, { partial: true }) as APIPatchedOrganizationRequest
  const response = await getOrganizationsApi().v1OrganizationsOrganizationPartialUpdate({
    id,
    aPIPatchedOrganizationRequest: request,
  })
  return createOrganization(response)
}

/**
 * Delete an organization
 */
export async function deleteOrganizationApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting organization:', idOrSelf)

  const id = resolveOrganizationId(idOrSelf)
  await getOrganizationsApi().v1OrganizationsOrganizationDestroy({ id })
}

